import { Link } from "react-router-dom";
import { LegalLayout, type Section } from "./LegalLayout.js";

/*
 * /privacy — privacy policy in plain English.
 *
 * Mirrors the brand's privacy posture from the README: "Wise Owl is
 * your private space. Your reflections are never sold and never used
 * to train external models." Each section reads warm, not legalese.
 *
 * Note: this is a reasonable, honest pre-launch privacy statement
 * for a self-help product running on Cloudflare. Before going live
 * commercially, run it past counsel — especially the GDPR / UK GDPR
 * / CCPA specifics and the data-processor list.
 */
const SECTIONS: Section[] = [
  {
    id: "summary",
    title: "The short version",
    body: (
      <>
        <div className="callout">
          <div className="label">In one breath</div>
          <p>
            Your reflections are yours. We hold them so the app can work.
            We don&apos;t sell them. We don&apos;t use them to train external
            AI. You can export or delete everything from your profile.
          </p>
        </div>
        <p>
          Everything below is the same thing said more slowly. Read what you
          want.
        </p>
      </>
    ),
  },
  {
    id: "collect",
    title: "What we collect",
    body: (
      <>
        <p>
          To run the app we hold the smallest set we can. Specifically:
        </p>
        <div className="def-table">
          <div className="def-row">
            <div className="k">Account</div>
            <div className="v">Your <strong>email</strong>, and (optionally) a first
              name you choose, a hashed password, and which language
              you&apos;ve picked.</div>
          </div>
          <div className="def-row">
            <div className="k">Reflections</div>
            <div className="v">The text of journal entries and mood selections you
              save. Stored alongside the time they were saved.</div>
          </div>
          <div className="def-row">
            <div className="k">Companion chats</div>
            <div className="v">Messages exchanged with the companion, kept so you
              can return to a conversation later.</div>
          </div>
          <div className="def-row">
            <div className="k">Streak &amp; stats</div>
            <div className="v">A count of days you&apos;ve completed a reset and
              how many reflections / chats you have. Not your content,
              just the counts.</div>
          </div>
          <div className="def-row">
            <div className="k">Device + network</div>
            <div className="v">Anonymized IP at the edge, browser/OS family,
              and approximate region (city-level). Used to keep the
              service stable and to spot abuse.</div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: "dont",
    title: "What we don't collect",
    body: (
      <>
        <p>
          Equally important — here&apos;s what we deliberately don&apos;t
          touch:
        </p>
        <ul>
          <li><strong>No precise location.</strong> No GPS, no street-level IP geolocation.</li>
          <li><strong>No advertising IDs.</strong> No IDFA / AAID / fingerprinting for ads.</li>
          <li><strong>No browsing history.</strong> We can&apos;t see what other sites you visit.</li>
          <li><strong>No contacts, no microphone, no camera.</strong> The app doesn&apos;t ask for these permissions.</li>
          <li><strong>No third-party trackers.</strong> No Google Analytics, no Meta Pixel, no ad networks.</li>
        </ul>
        <p>
          See <Link to="/analytics">how we measure usage</Link> for the
          self-hosted, privacy-respecting analytics we do run.
        </p>
      </>
    ),
  },
  {
    id: "use",
    title: "How we use it",
    body: (
      <>
        <p>We use the data above for these reasons only:</p>
        <h3>To run the app</h3>
        <p>
          Show you your own reflections, mood graph, and chat history.
          Send 6-digit sign-in codes and password-reset emails. Keep your
          session signed in.
        </p>
        <h3>To make the AI work</h3>
        <p>
          When you write a reflection or message the owl, we pass the text
          to a Large Language Model so it can respond. The provider sees
          the message only long enough to generate a reply — they do not
          retain it to train their models (we use an enterprise contract
          that prohibits this).
        </p>
        <h3>To keep things stable and safe</h3>
        <p>
          Network logs help us notice outages, rate-limit abuse, and
          investigate suspected break-in attempts. These logs roll over on
          a short cycle.
        </p>
        <h3>To improve the product</h3>
        <p>
          We look at <em>anonymized, aggregated</em> usage — which screens
          people open, how many resets are completed in a day, which
          features they ignore — to know what to fix next. The numbers do
          not include the text of anyone&apos;s reflections.
        </p>
      </>
    ),
  },
  {
    id: "where",
    title: "Where your data lives",
    body: (
      <>
        <p>
          Wise Owl Talks runs on <strong>Cloudflare</strong> — Workers for the
          API and Pages for the static app. Cloudflare&apos;s servers are
          spread across multiple regions and your data may be processed in
          any of them when you use the service.
        </p>
        <p>The processors we use today:</p>
        <div className="def-table">
          <div className="def-row">
            <div className="k">Cloudflare</div>
            <div className="v">Hosting, edge compute, edge KV/D1 (planned) for
              reflections and accounts. <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noreferrer">Privacy policy ↗</a></div>
          </div>
          <div className="def-row">
            <div className="k">An LLM provider</div>
            <div className="v">For Companion replies and reflection insights.
              Enterprise contract; no training on our requests.</div>
          </div>
          <div className="def-row">
            <div className="k">Email provider</div>
            <div className="v">For sending 6-digit sign-in codes and
              password-reset links. The provider sees only the recipient
              email and the message body we hand them.</div>
          </div>
        </div>
        <p>
          We add to or change this list when something material changes
          and re-stamp this page&apos;s &ldquo;last updated&rdquo; date.
        </p>
      </>
    ),
  },
  {
    id: "rights",
    title: "Your rights",
    body: (
      <>
        <p>Wherever you are in the world, you can:</p>
        <ul>
          <li><strong>See everything we hold</strong> — open{" "}
            <Link to="/profile">Profile → Privacy → Show me what I look like to Wise Owl Talks</Link>.</li>
          <li><strong>Export everything</strong> — Profile → Privacy → Export
            everything. You get a JSON file of every reflection, mood,
            and chat.</li>
          <li><strong>Pause your account</strong> — Profile → Privacy. Your
            data sits idle. Nothing is shown anywhere. Reversible.</li>
          <li><strong>Delete your account</strong> — Profile → Privacy.
            Reversible for 30 days, then gone. We can&apos;t restore it
            after that.</li>
          <li><strong>Correct anything</strong> — write to{" "}
            <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a>.</li>
          <li><strong>Object or restrict</strong> — write to us. We&apos;ll
            answer within a few working days.</li>
        </ul>
        <p>
          If you&apos;re in the UK or EU, you also have the right to
          complain to your local data-protection authority. We&apos;d
          rather hear from you first — but the option is yours.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "How long we keep things",
    body: (
      <>
        <ul>
          <li><strong>Reflections, chats, moods</strong> — until you delete
            them or close your account.</li>
          <li><strong>Account record</strong> — until you delete it. We keep
            a minimal record of <em>which accounts existed</em> for up to
            12 months after deletion, only to enforce safety bans and to
            comply with legal duties.</li>
          <li><strong>Email codes and reset links</strong> — 10 minutes for
            codes, 30 minutes for reset links. Then dropped.</li>
          <li><strong>Network and abuse logs</strong> — 30 days, then
            rotated.</li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies and analytics",
    body: (
      <>
        <p>
          We try to use as few cookies as possible. The ones we do set are
          either strictly necessary (sign-in session) or are used for
          first-party, anonymous usage measurement.
        </p>
        <p>
          For the detailed picture, see <Link to="/analytics">how we measure
          usage</Link>. The short version is: no third-party trackers, no
          ad networks, and you can turn off non-essential measurement from
          <Link to="/profile"> your profile</Link>.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "Children",
    body: (
      <>
        <p>
          Wise Owl Talks is not for anyone under 16. We don&apos;t knowingly
          collect data from children. If a parent or guardian believes a
          child has an account, write to{" "}
          <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a> and we&apos;ll
          close it and delete the data quickly.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <>
        <p>
          When this page changes in a meaningful way, we&apos;ll show a
          notice inside the app and update the &ldquo;last updated&rdquo;
          date at the top. Small fixes (typos, clarifications) just get
          the date stamp.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <p>
          For anything in this page — a question, a correction, a request,
          a worry — write to{" "}
          <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a>. A real
          person reads everything and replies within two working days.
        </p>
        <p className="reflect">&ldquo;Privacy isn&apos;t a wall. It&apos;s a small kept space.&rdquo;</p>
      </>
    ),
  },
];

export const Privacy = () => (
  <LegalLayout
    page="privacy"
    eyebrow="Privacy"
    title={<>Private by default.</>}
    lede="What we collect, how we use it, what we never do, and how to take it back."
    updatedISO="2026-05-21"
    sections={SECTIONS}
  />
);
